import { Link, useSearchParams } from 'react-router-dom';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api, type Dashboard } from '../api';
import { AndamentoEconomico } from './andamento';
import { Segnali } from './segnali';
import { Caricamento, Errore, Etichetta, Importo, Linguette, LinkFattura, Scheda, Vuoto, useCarico } from '../componenti/base';
import { euroCompatto, formattaEuro, formattaData, plurale } from '../formato';
import { useSessione } from '../main';

/**
 * Quanto è vecchio uno scaduto conta più del fatto che lo sia: un documento
 * fermo da tre giorni e uno fermo da dieci anni chiedono azioni diverse, e
 * un'etichetta piatta «scaduta» li fa sembrare uguali.
 */
function etichettaRitardo(dataISO: string, oggiISO: string): string {
  const giorni = Math.round((Date.parse(oggiISO) - Date.parse(dataISO)) / 86_400_000);
  if (giorni < 30) return `scaduta da ${plurale(giorni, 'giorno', 'giorni')}`;
  if (giorni < 730) return `scaduta da ${plurale(Math.round(giorni / 30), 'mese', 'mesi')}`;
  return `scaduta da ${plurale(Math.round(giorni / 365), 'anno', 'anni')}`;
}

export function Cruscotto() {
  const { organizzazione } = useSessione();
  // La linguetta sta nell'indirizzo e non solo nella memoria del componente:
  // ricaricando si resta dov'eri, e un collegamento può portare direttamente
  // sull'andamento. `replace` per non riempire la cronologia del browser di
  // passaggi fra due schede della stessa pagina.
  const [parametri, setParametri] = useSearchParams();
  const scheda = parametri.get('scheda') === 'andamento' ? 'andamento' : 'cassa';
  const { dati, caricamento, errore, ricarica } = useCarico(() => api.get<Dashboard>('/api/dashboard'), []);

  if (caricamento) return <Caricamento />;
  if (errore || !dati) return <div className="p-6"><Errore testo={errore ?? 'Dati non disponibili'} riprova={ricarica} /></div>;

  const { previsione, conti, kpi, prossimeScadenze } = dati;
  const futuri = previsione.periodi.filter((p) => p.collocazione !== 'PASSATO');
  const oggiISO = new Date().toISOString().slice(0, 10);
  // La sezione promette «prossimi 45 giorni»: se quello che arriva è invece
  // tutto arretrato, va detto — altrimenti l'assenza di righe future si legge
  // come «nessuna scadenza in vista» invece che «arretrato da smaltire».
  const tutteScadute = prossimeScadenze.length > 0 && prossimeScadenze.every((s) => s.dataCassaAttesa < oggiISO);
  const unitaPeriodo = previsione.granularita === 'SETTIMANA' ? 'settimana' : 'mese';
  const unitaPeriodoPlurale = previsione.granularita === 'SETTIMANA' ? 'settimane' : 'mesi';

  const serie = futuri.map((p) => ({
    nome: p.etichetta,
    saldo: p.saldoFinaleCent === null ? null : p.saldoFinaleCent / 100,
    // Senza l'arretrato, come il saldo che la linea disegna: a settembre le
    // barre contavano 165 mila di fatture passive scadute prima del mese e
    // mostravano 200 mila di uscite, mentre il saldo — giustamente — non le
    // conta. Un grafico le cui barre e la cui linea dicono cose diverse non
    // si legge.
    entrate: (p.entrateCent - p.perFonte.SCADUTI.entrateCent) / 100,
    uscite: -(p.usciteCent - p.perFonte.SCADUTI.usciteCent) / 100,
    allerta: p.inAllerta,
  }));

  const contiInCassa = conti.filter((c) => c.contribuisceAllaCassa);
  const contiFuoriCassa = conti.filter((c) => !c.contribuisceAllaCassa);
  const ultimoImport = contiInCassa
    .map((c) => c.dataUltimoImport)
    .filter((d): d is string => Boolean(d))
    .sort()
    .at(-1);

  return (
    <div className="pagina space-y-5">
      <header>
        <h1 className="text-lg font-semibold tracking-tight">Cruscotto</h1>
        <p className="text-sm text-inchiostro-500">
          {organizzazione.ragioneSociale}
          {ultimoImport && (
            <>
              {' · '}
              {/* Senza collegamento bancario un saldo vale quanto è recente il
                  file: tacerlo fa leggere come cassa di oggi una cassa vecchia. */}
              <span title="Data dell'ultimo movimento importato">
                movimenti aggiornati al {formattaData(ultimoImport, organizzazione.formatoData)}
              </span>
            </>
          )}
        </p>
      </header>

      {/* Due domande diverse, due linguette. «Quando arrivano i soldi» e «il
          lavoro sta pagando» si guardano in momenti diversi, e impilarle
          spingeva in fondo alla pagina proprio la cassa, che è quella che si
          apre tutti i giorni. */}
      <Linguette
        voci={[
          { chiave: 'cassa', testo: 'Cassa' },
          { chiave: 'andamento', testo: 'Andamento economico' },
        ]}
        attiva={scheda}
        onSceglie={(c) => setParametri(c === 'cassa' ? {} : { scheda: c }, { replace: true })}
      />

      {scheda === 'andamento' ? (
        <AndamentoEconomico />
      ) : (
        <>
      {/* Lo stesso controllo del previsionale: i giorni, non le fine mese. */}
      {previsione.primoGiornoSottoSoglia && previsione.minimoDiCassa ? (
        <div className="rounded-lg border border-allerta/30 bg-allerta-chiaro p-4">
          <p className="text-sm font-semibold text-allerta">
            Il {formattaData(previsione.primoGiornoSottoSoglia.data, organizzazione.formatoData)} la cassa prevista
            scende sotto la soglia
          </p>
          <p className="mt-1 text-sm text-allerta/90">
            Il punto più basso è {formattaEuro(previsione.minimoDiCassa.saldoCent)} il{' '}
            {formattaData(previsione.minimoDiCassa.data, organizzazione.formatoData)}, contro una soglia minima di{' '}
            {formattaEuro(previsione.sogliaMinimaCent)}: mancano{' '}
            {formattaEuro(previsione.sogliaMinimaCent - previsione.minimoDiCassa.saldoCent)}.{' '}
            <Link to="/previsionale" className="underline underline-offset-2">
              Vedi da cosa dipende
            </Link>
            .
          </p>
        </div>
      ) : previsione.primaAllerta ? (
        <div className="rounded-lg border border-allerta/30 bg-allerta-chiaro p-4">
          <p className="text-sm font-semibold text-allerta">
            Cassa sotto la soglia a {previsione.primaAllerta.etichetta}
          </p>
          <p className="mt-1 text-sm text-allerta/90">
            Il saldo previsto a fine periodo è {formattaEuro(previsione.primaAllerta.saldoFinaleCent ?? 0)}, contro una soglia
            minima di {formattaEuro(previsione.sogliaMinimaCent)}.{' '}
            <Link to="/previsionale" className="underline underline-offset-2">
              Vedi da cosa dipende
            </Link>
            .
          </p>
        </div>
      ) : (
        // Quando non c'è un'allerta la pagina non deve restare muta sulla
        // previsione: il silenzio si legge come «non ho controllato», non
        // come «va tutto bene». Lo stesso messaggio, in positivo, del previsionale.
        futuri.length > 0 && (
          <div className="rounded-lg border border-incassi/30 bg-incassi-chiaro p-3 text-sm text-incassi">
            Nessun giorno, da oggi in avanti, scende sotto la soglia di{' '}
            {formattaEuro(previsione.sogliaMinimaCent)} nei prossimi{' '}
            {plurale(futuri.length, unitaPeriodo, unitaPeriodoPlurale)}.
          </div>
        )
      )}

      <Segnali />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Riquadro titolo="Cassa di oggi" valore={previsione.cassaAttualeCent} tono="saldo">
          <p className="mt-2 space-y-0.5 text-xs text-inchiostro-500">
            {contiInCassa.map((c) => (
              <span key={c.id} className="block truncate">
                {c.nome} · <span className="num">{formattaEuro(c.saldoCorrenteCent)}</span>
              </span>
            ))}
          </p>
          {contiFuoriCassa.length > 0 && (
            <p className="mt-2 border-t border-inchiostro-100 pt-2 text-xs text-inchiostro-500">
              {/* L'anticipo fatture non è liquidità: è fido utilizzato. */}
              Fuori dalla cassa: <span className="num">{formattaEuro(previsione.fuoriCassaCent)}</span> di fido
              utilizzato
            </p>
          )}
        </Riquadro>

        <Riquadro titolo="Da incassare" valore={kpi.da_incassare} tono="incassi">
          {kpi.scaduto_attivo > 0 && (
            <p className="mt-2 text-xs text-allerta">
              di cui <span className="num">{formattaEuro(kpi.scaduto_attivo)}</span> oltre la scadenza
            </p>
          )}
        </Riquadro>

        <Riquadro titolo="Da pagare" valore={kpi.da_pagare} tono="uscite">
          {kpi.scaduto_passivo > 0 && (
            <p className="mt-2 text-xs text-allerta">
              di cui <span className="num">{formattaEuro(kpi.scaduto_passivo)}</span> oltre la scadenza
            </p>
          )}
        </Riquadro>

        {/* Stesso corpo a 2xl delle altre tre: è un riquadro d'azione, non di
            contorno, e a leggerlo più piccolo sembrava meno urgente di quanto sia. */}
        <Riquadro titolo="Da sistemare" valore={null} tono="neutro">
          <p className="mt-1 text-2xl">
            <Link to="/scadenzario" className="num font-semibold text-inchiostro-800 underline underline-offset-2">
              {kpi.n_scadute}
            </Link>
          </p>
          <p className="text-xs text-inchiostro-500">
            {plurale(kpi.n_scadute, 'documento scaduto', 'documenti scaduti').replace(/^\d+\s/, '')}
          </p>
          <p className="mt-2 border-t border-inchiostro-100 pt-2 text-xs">
            <Link to="/riconciliazione" className="underline underline-offset-2">
              <span className="num font-semibold">{kpi.movimentiDaSanare}</span> movimenti senza categoria
            </Link>
            {kpi.movimentiDaSanare > 0 && (
              <span className="mt-1 block text-inchiostro-500">Sono in «Da identificare».</span>
            )}
          </p>
        </Riquadro>
      </div>

      {/* Di quello che c'è in banca, quanto non è dell'azienda. Indicativo e solo
          qui: la regola e il perché stanno in `@mastro/core/iva`. */}
      <Scheda
        titolo="Quanto della cassa è davvero libero"
        sottotitolo="La cassa di oggi senza i soldi che sono già dello Stato"
      >
        <div className="grid gap-x-10 gap-y-4 sm:grid-cols-2">
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between gap-4">
              <span>Cassa di oggi</span>
              <Importo cent={previsione.cassaAttualeCent} tono="saldo" />
            </div>
            <div className="flex justify-between gap-4">
              <span>− F24 registrati e non ancora versati</span>
              <Importo cent={-dati.iva.f24DaVersareCent} tono="uscite" />
            </div>
            {dati.iva.stato === 'CALCOLATA' && (
              <div className="flex justify-between gap-4">
                <span>
                  − IVA da tenere da parte <Etichetta testo="indicativa" />
                </span>
                <Importo cent={-dati.iva.daAccantonareCent} tono="uscite" />
              </div>
            )}
            <div className="flex justify-between gap-4 border-t border-inchiostro-200 pt-2 text-base">
              <span className="font-semibold">Cassa libera dopo IVA e F24</span>
              <Importo cent={dati.iva.cassaLiberaCent} tono="saldo" grassetto />
            </div>

            {/* Due numeri diversi, e confonderli è proprio l'errore che il
                programma deve evitare. Quello sopra è la cassa di oggi senza i
                soldi dello Stato; questo è quanto si può impegnare senza che, nei
                prossimi mesi, la cassa scenda sotto la soglia — e si misura sul
                punto più basso, giorno per giorno. «Cassa libera 8.250 €» con il
                fondo a −2.180 € il mese dopo si leggeva come «ne posso spendere
                cinquemila». */}
            {previsione.minimoDiCassa && previsione.sogliaMinimaCent > 0 && (
              <div className="rounded-md border border-inchiostro-200 bg-inchiostro-50 p-2.5">
                <div className="flex justify-between gap-4 text-base">
                  <span className="font-semibold">Disponibilità per nuove spese</span>
                  <Importo
                    cent={Math.max(previsione.minimoDiCassa.saldoCent - previsione.sogliaMinimaCent, 0)}
                    tono="saldo"
                    grassetto
                  />
                </div>
                <p className="mt-1 text-xs text-inchiostro-500">
                  {previsione.minimoDiCassa.saldoCent >= previsione.sogliaMinimaCent ? (
                    <>
                      Quanto si può impegnare senza che, nei prossimi{' '}
                      {plurale(futuri.length, unitaPeriodo, unitaPeriodoPlurale)}, la cassa prevista scenda sotto la
                      soglia di {formattaEuro(previsione.sogliaMinimaCent)}: il punto più basso è{' '}
                      {formattaEuro(previsione.minimoDiCassa.saldoCent)} il{' '}
                      {formattaData(previsione.minimoDiCassa.data, organizzazione.formatoData)}.
                    </>
                  ) : (
                    <span className="text-allerta">
                      Prima di assumere nuovi impegni occorre recuperare almeno{' '}
                      <span className="num font-semibold">
                        {formattaEuro(previsione.sogliaMinimaCent - previsione.minimoDiCassa.saldoCent)}
                      </span>{' '}
                      di liquidità prevista: il {formattaData(previsione.minimoDiCassa.data, organizzazione.formatoData)}{' '}
                      la cassa è attesa a {formattaEuro(previsione.minimoDiCassa.saldoCent)}, sotto la soglia di{' '}
                      {formattaEuro(previsione.sogliaMinimaCent)}.{' '}
                      <Link to="/previsionale" className="underline underline-offset-2">
                        Vedi perché
                      </Link>
                      .
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
          <div className="space-y-2 text-xs text-inchiostro-500">
            {dati.iva.stato === 'CALCOLATA' ? (
              <>
                <p>
                  L’IVA delle fatture dal {formattaData(dati.iva.dal, organizzazione.formatoData)} a oggi, cioè dopo
                  l’ultimo periodo già in un F24 registrato (fino al{' '}
                  {formattaData(dati.iva.ultimoPeriodoCoperto.al, organizzazione.formatoData)}):{' '}
                  <span className="num">{formattaEuro(dati.iva.debitoCent)}</span> sulle fatture emesse meno{' '}
                  <span className="num">{formattaEuro(dati.iva.creditoCent)}</span> su quelle ricevute, in{' '}
                  {plurale(dati.iva.documenti, 'documento', 'documenti')}.
                  {dati.iva.nettoCent < 0 && ' Il saldo è a credito: non c’è niente da tenere da parte.'}
                </p>
                <p>
                  È indicativa: l’importo esatto lo dirà l’F24 del commercialista, che tiene conto di compensazioni e
                  casi particolari. Lo split payment è già escluso. Non entra nel previsionale, dove l’IVA arriva con
                  l’F24 registrato.
                </p>
              </>
            ) : (
              <p>
                Per sapere da quando contare l’IVA delle fatture serve almeno un F24 IVA registrato con il codice
                tributo (per esempio 6007 per luglio).{' '}
                <Link to="/f24" className="underline underline-offset-2">
                  Registra un F24
                </Link>
                .
              </p>
            )}
          </div>
        </div>
      </Scheda>

      <Scheda
        titolo="Previsione di cassa"
        sottotitolo="Saldo previsto a fine periodo, entrate e uscite attese"
        azioni={
          <Link to="/previsionale" className="bottone-secondario">
            Apri il previsionale
          </Link>
        }
      >
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={serie} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid stroke="var(--color-inchiostro-200)" vertical={false} />
              <XAxis dataKey="nome" tick={{ fontSize: 11, fill: 'var(--color-inchiostro-500)' }} tickLine={false} />
              {/* Un asse solo con base a zero: l'allineamento fra due scale è
                  arbitrario e inventa correlazioni che non ci sono. */}
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--color-inchiostro-500)' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => euroCompatto(v * 100)}
              />
              <Tooltip
                formatter={(v, n) => [formattaEuro(Math.abs(Number(v)) * 100), String(n)]}
                labelClassName="font-medium"
                contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid var(--color-inchiostro-200)' }}
              />
              <ReferenceLine y={0} stroke="var(--color-inchiostro-400)" />
              {previsione.sogliaMinimaCent !== 0 && (
                <ReferenceLine
                  y={previsione.sogliaMinimaCent / 100}
                  stroke="var(--color-allerta)"
                  strokeDasharray="4 3"
                  label={{ value: 'soglia', fontSize: 10, fill: 'var(--color-allerta)', position: 'insideTopLeft' }}
                />
              )}
              <Bar dataKey="entrate" name="Entrate" fill="var(--color-incassi)" fillOpacity={0.85} radius={[2, 2, 0, 0]} />
              <Bar dataKey="uscite" name="Uscite" fill="var(--color-uscite)" fillOpacity={0.85} radius={[0, 0, 2, 2]} />
              <Line
                type="monotone"
                dataKey="saldo"
                name="Saldo previsto"
                stroke="var(--color-saldo)"
                strokeWidth={2.5}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Scheda>

      <Scheda
        titolo="Prossime scadenze"
        sottotitolo={
          tutteScadute
            ? 'Le più vecchie ancora aperte — nessuna risulta nei prossimi 45 giorni'
            : 'Nei prossimi 45 giorni, alla data di cassa attesa'
        }
        azioni={
          <Link to="/scadenzario" className="bottone-secondario">
            Apri lo scadenzario
          </Link>
        }
        senzaPadding
      >
        {prossimeScadenze.length === 0 ? (
          <Vuoto titolo="Nessuna scadenza nei prossimi 45 giorni" />
        ) : (
          <div className="overflow-x-auto">
            <table className="dati">
              <thead>
                <tr>
                  <th>Data attesa</th>
                  <th>Documento</th>
                  <th>Controparte</th>
                  <th className="text-right">Importo</th>
                </tr>
              </thead>
              <tbody>
                {prossimeScadenze.map((s, i) => (
                  <tr key={`${s.documentoId}-${i}`}>
                    <td className="num whitespace-nowrap">
                      {formattaData(s.dataCassaAttesa, organizzazione.formatoData)}
                      {s.dataCassaAttesa < oggiISO && (
                        <span className="ml-2">
                          <Etichetta testo={etichettaRitardo(s.dataCassaAttesa, oggiISO)} tono="allerta" />
                        </span>
                      )}
                    </td>
                    <td>
                      <LinkFattura id={s.documentoId} className="underline underline-offset-2">
                        {s.numero}
                      </LinkFattura>
                      <span className="ml-2 text-xs text-inchiostro-500">
                        {s.tipo === 'ATTIVA' ? 'attiva' : 'passiva'}
                      </span>
                    </td>
                    <td>{s.controparte ?? '—'}</td>
                    <td className="text-right">
                      <Importo cent={s.importoCent} tono={s.tipo === 'ATTIVA' ? 'incassi' : 'uscite'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Scheda>
      </>
      )}
    </div>
  );
}

function Riquadro({
  titolo,
  valore,
  tono,
  children,
}: {
  titolo: string;
  valore: number | null;
  tono: 'saldo' | 'incassi' | 'uscite' | 'neutro';
  children?: React.ReactNode;
}) {
  // Il bordo superiore della scheda prende la tinta del dato (come le box
  // colorate di AdminLTE), senza intingere il resto della cornice.
  const bordo: Record<typeof tono, string> = {
    saldo: 'border-t-saldo',
    incassi: 'border-t-incassi',
    uscite: 'border-t-uscite',
    neutro: 'border-t-inchiostro-300',
  };
  return (
    <div className={`scheda p-4 ${bordo[tono]}`}>
      <p className="text-xs font-medium uppercase tracking-wide text-inchiostro-500">{titolo}</p>
      {valore !== null && (
        <p className="mt-1 text-2xl">
          <Importo cent={valore} tono={tono} grassetto />
        </p>
      )}
      {children}
    </div>
  );
}
